<template>
  <BaseButton
    class="reset-btn-styling text-start d-flex align-items-center mr-16"
    variant="link"
    size="sm"
    @click="play"
  >
    <BaseSpinner v-if="isLoading" size="sm" />
    <PauseIcon v-else-if="isPlaying" class="pause" />
    <PlayIcon v-else class="play" />
    {{
      trackDuration ? formatTime(leftTrackTime) : $label('page.audioExample')
    }}
    <audio
      ref="audio"
      :src="mp3"
      :artist="author.name"
      preload="none"
      :title="bookTitle"
    ></audio>
  </BaseButton>
</template>

<script>
import PlayIcon from '~/assets/icons/play.svg'
import PauseIcon from '~/assets/icons/pause.svg'

export default {
  components: {
    PlayIcon,
    PauseIcon
  },
  props: {
    mp3: {
      type: String,
      required: true
    },
    bookTitle: {
      type: String,
      default: ''
    },
    author: {
      type: Object,
      default: () => ({})
    },
    cover: {
      type: String,
      default: ''
    }
  },
  setup() {
    const config = useRuntimeConfig()
    const imageResizeUrl = config.public.IMAGE_RESIZE_URL

    return {
      imageResizeUrl
    }
  },
  data() {
    return {
      isPlaying: false,
      isLoading: false,
      leftTrackTime: 0,
      trackDuration: 0
    }
  },
  mounted() {
    const { audio } = this.$refs
    audio.onplay = this.onPlay
    audio.onpause = this.onPause
    audio.onended = this.onEnded
    audio.oncanplay = this.onCanPlay
    audio.onplaying = this.onCanPlay
    audio.onerror = this.onError
  },
  beforeUnmount() {
    this.pause()
  },
  methods: {
    async play() {
      if (this.isPlaying) return this.pause()

      try {
        this.isPlaying = true
        await this.$refs.audio.play()
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.onError(error)
        }
      }
    },
    pause() {
      this.$refs.audio?.pause()
      this.isPlaying = false
    },
    onPlay() {
      this.updateTrackTime()
      this.pushTrackingEvent()
      this.isLoading = true
    },
    onEnded() {
      this.trackDuration = 0
      this.isPlaying = false
    },
    updateTrackTime() {
      if (this.isPlaying) {
        const { duration, currentTime } = this.$refs.audio

        this.trackDuration = duration || 0
        this.leftTrackTime = duration - currentTime
        setTimeout(this.updateTrackTime, 300)
      }
    },
    formatTime(seconds) {
      return new Date(seconds * 1000).toISOString().substr(14, 5)
    },
    onCanPlay() {
      this.isLoading = false
      this.isPlaying = true
      this.setMetadata()
    },
    onError(event) {
      this.isPlaying = false
      this.isLoading = false
      const error = event.target ? event.target.error : event

      this.$toast.error(this.$label('messages.errorOccuredMessage'))
      this.$sentryCaptureException(error)
    },
    pushTrackingEvent() {
      this.$gtmTracking.push({
        event: 'interact_event',
        eventAction: 'play',
        eventCategory: 'MonthlyBooksDetails || audioPreview',
        eventLabel: this.bookTitle
      })
    },
    setMetadata() {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: this.bookTitle,
          artist: this.author.name,
          artwork: [
            {
              src:
                this.cover ||
                `${this.imageResizeUrl}?image=fe-assets/book-placeholder.png`
            }
          ]
        })
      }
    },
    onPause() {
      this.isPlaying = false
    }
  }
}
</script>

<style lang="scss" scoped>
.play,
.pause,
.spinner-border {
  margin-left: 2px;
  margin-right: 10px;
}

.play,
.pause {
  height: 16px;
  width: 15.1px;
}

.spinner-border {
  height: 12px;
  width: 12px;
}

.btn-link:focus {
  text-decoration: none;
}

.btn {
  color: $black;
  text-decoration: underline;
}
</style>
